import React, { useState, useEffect, useCallback } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { EllipsisVerticalIcon, PlusIcon } from '@heroicons/react/20/solid'
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams } from 'react-router-dom';
import HeadingCp from '../components/HeadingCp';
import Modal from '../components/Modal';
import ShortDateFormat from '../components/ShortDateFormat';
import PhoneNumber from '../components/PhoneNumber';
import LoadingSpinner from '../components/LoadingSpinner';
import Accordion from '../components/Accordion';
import { TrashIcon } from '@heroicons/react/24/outline';
import SelectParts from '../components/SelectParts';
import ServiceGenFileLocation from '../components/ServiceGenFileLocation';
import LinkText from '../components/LinkText';

const TechServiceDetail = () => {
  const appRoot = process.env.REACT_APP_ROOT_URL;
  const navigate = useNavigate();
  const [serviceCalls, setServiceCalls] = useState([]);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [customerItem, setCustomerItem] = useState({});
  
  const [noChargeSet, setNoChargeSet] = useState(false);
  const [isNoCharge, setIsNoCharge] = useState(false);

  const [additionalTechs, setAdditionalTechs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedTech, setSelectedTech] = useState(null);
  const [message, setMessage] = useState('');
    
  const [techNotes, setTechNotes] = useState('');
  const [serviceComplete, setServiceComplete] = useState({ completeTicket: false, notCompletedTicket: false });
  const [openIndex, setOpenIndex] = useState(null);

  // hours
  const [hourList, setHourList] = useState([]);
  const [service, setService] = useState({total_hours:'', hour_type: '' });

  // adding tech
  const [additionTech, setAdditionTech] = useState([]);
  const [addTech, setAddTech] = useState({additional_tech:''});

  // adding parts
  const [servicePart, setServicePart] = useState({ part: '', quantity: 1 });
  const [parts, setParts] = useState([]);
  const [selectParts, setSelectedParts] = useState(null);  
  const [skipPart, setSkipPart] = useState(false);
  const [partsList, setPartsList] = useState([]);

  const [keyController, setKeyController] = useState(true);

   // Customer email/signature states
  const [serviceEmail, setServiceEmail] = useState({
    customerEmail: '',
    customerPrint: '',
    noEmail: false
  });
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [customerEmailMessage, setCustomerEmailMessage] = useState('');
  const [activateSignature, setActivateSignature] = useState(false);
  const [submitEmailButton, setSubmitEmailButton] = useState(false);

   //below for folder creation and image upload
  const [filesToUpload, setFilesToUpload] = useState([]);  // an array of files
  const [folderId, setFolderId] = useState(null);          // store created folder ID
  const [selectedFiles, setSelectedFiles] = useState([]);  // an array of files
  const [isModalFileGenOpen, setIsModalFileGenOpen] = useState(false);
  
  const [isInternalOpen, setIsInternalOpen] = useState(false);

  const openInMaps = () => {
    const fullAddress = [
      serviceCalls?.ADDRESS
    ].filter(Boolean).join(' ');
    if (!fullAddress.trim()) {
      toast?.error?.("No job site address available", { position: 'top-center' });
      return;
    }
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`, '_blank');
  };  
  
  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    setLoading(true);
    fetchServiceCalls(); //service call
    fetchEmployees(); //tech list
   }, [apiUrl, navigate, id]);

    // charge or no charge
  useEffect(() => {
    if (serviceCalls?.TYPE) {
      setIsNoCharge(serviceCalls.TYPE === 'NO CHARGE');
    }
  }, [serviceCalls]);
 
  const fetchServiceCalls = async () => { 
    try {
      const response = await axios.get(`${apiUrl}/service_crud.php?getDetailTech=${id}`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      if (response.data[0] === 'success') {
        const serviceCallData = response.data[1] ?? [];
        
        // Set service call data
        setServiceCalls(serviceCallData);

        //hours
        const listHours = Array.isArray(response.data[1].add_hours) ? response.data[1].add_hours : false;
        setHourList(listHours || [])
        // setActivateSignature(listHours || true)
        setActivateSignature(listHours === false ? false : true)

        //addition tech
        const additionTech = Array.isArray(response.data[1].additional_tech) ? response.data[1].additional_tech : [];
        setAdditionTech(additionTech || [])

        //adding parts
        const partDevice = Array.isArray(response.data[1].add_parts) ? response.data[1].add_parts : [];
        setPartsList(partDevice || [])
        
        //handle techNotes
        const addTechNotes = serviceCallData.TECHNOTES || ''; // Ensure TECHNOTES is set to an empty string if it's not available
        setTechNotes(addTechNotes);

        //handle customer email / customer print name
        const customerEmail = serviceCallData.CUSTOMEREMAIL || ''; 
        const customerPrint = serviceCallData.CUSTOMER_PRINT || '';
        if(serviceCallData.CUSTOMEREMAIL === 'noemail')
        {
          setServiceEmail({   customerEmail: customerEmail, customerPrint: customerPrint, noEmail:true});          
        }
        else
        {
          setServiceEmail({   customerEmail: customerEmail, customerPrint: customerPrint, noEmail:false});          
        }

        if(customerPrint && customerEmail)
        {
          setSubmitEmailButton(true);
        }

        // Handle no charge logic
        setIsNoCharge(serviceCallData?.TYPE === 'NO CHARGE');
        
      } else {
        toast.error(response.data.error, { position: "top-center" });
      }
      
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", { position: "top-center" });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", { position: "top-center" });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${apiUrl}/service_crud.php?employee_list=true`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      const serviceCalls = Array.isArray(response.data) ? response.data : [];
      if (serviceCalls.length === 0) {
        toast.error("No service calls found!", { position: "top-center" });
      }
      setEmployees(response.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", { position: "top-center" });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", { position: "top-center" });
      }
    } finally {
      setLoading(false);
    }
  };  
  
  const handleCheckboxClick = async (id, currentType) => { //done
    const newType = currentType === 'NO CHARGE' ? 'CHARGE' : 'NO CHARGE';
    setLoading(true);

    try {
      const response = await axios.post(`${apiUrl}/service_crud.php`, {
        status: 'handleCharge',
        id: id,
        type: newType
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data[0] === 'success') {
        // After successful API response, update the TYPE and checkbox state
        setServiceCalls((prevCall) => ({
          ...prevCall,
          TYPE: newType
        }));

        // Sync checkbox with updated type
        setIsNoCharge(newType === 'NO CHARGE');
        
        toast.success(`Service call updated to ${newType}!`, { position: 'top-center' });
      } else {
        toast.info('Failed to change type.', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error('Session expired. Please login again.', { position: 'top-center' });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

// ************** adding hours below ******************
  const handleHoursChange = (e) => {
    setService({ ...service, total_hours: e.target.value });
  };

  const handleSubmitHours = async () => {
    if(service.hour_type === '' || service.total_hours === '')
    {
      toast.info('Missing hour type.', { position: 'top-center' });
      setLoading(false);
      return; // Prevent adding duplicate tech 
    }

    const bodyData = {
      status: "addHours",
      hour_type: service.hour_type,
      total_hours: service.total_hours,
      service_call_id: id
    };

      try {
        const response = await axios.post(`${apiUrl}/service_crud.php`, bodyData, {
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.data[0] === 'success') {
          // const addHours = {total_hours: service.total_hours};
          setHourList([...hourList, { total_hours: service.total_hours, hour_type: service.hour_type }]);
          setActivateSignature(true)

            // setServiceCalls((prevServiceCalls) => ({
            //   ...prevServiceCalls, // Keep the previous state
            //   additional_tech: [...(prevServiceCalls.add_hours || []), hourList], // Append newTech to the existing additional_tech array
            // }));

          toast.success('Tech added!', {
            position: 'top-center',
          });
          toggleAccordion(0);
        } else {
          toast.info("Failed to add tech", { position: "top-center" });
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          toast.error("Session expired. Please login again.", {
            position: "top-center"
          });
          navigate('/login');
        } else if (error.response && error.response.status === 403) {
          toast.error("Oops!! You don't have access to this area. Speak to the CFO.", {
            position: "top-center"
          });
        }
      } finally {
        setLoading(false);
      }

      setOpenIndex(null);
      setService({total_hours:'', hour_type:''})
    // }
  };

  const handleHourTypeChange = (e) => {
    setService({ ...service, hour_type: e.target.value });
  };
  
  const handleDeleteHours = async(hrsID) => {
    setLoading(true);
    const bodyAddData = {
      status: "removeAddHours",
      hours_id: hrsID,
    };
    try {
      const response = await axios.post(`${apiUrl}/service_crud.php`, bodyAddData, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data[0] === 'success') {
          setHourList(hourList.filter((hrs) => hrs.id !== hrsID));
          toast.success('Hrs removed!', { position: 'top-center' });
          setActivateSignature(false)
          // toggleAccordion();
      } else {
        toast.info("Failed to remove Hrs", { position: "top-center" });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", { position: "top-center" });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  }
// ************** adding hours above *****************

// ************** adding tech notes below *****************
  const handleTechNotesChange = (e) => {
    setTechNotes(e.target.value); // Correctly update the state with the input value
  };

  const handleSubmitTechNotes = async () => {
    setLoading(true);

    const bodyData = {
      status: "addTechNotes", // Assuming this is the correct status for your backend
      techNotes: techNotes,  // Send the updated tech notes
      id: id,  // Assuming id is the service call ID
    };

    try {
      const response = await axios.post(`${apiUrl}/service_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data[0] === 'success') {
        setServiceCalls((prevState) => ({
          ...prevState,
          TECHNOTES: techNotes, // Update the state with the new tech notes
        }));
        toast.success('Tech notes updated!', { position: 'top-center' });
        toggleAccordion(3); // Close the accordion after submission if needed
      } else {
        toast.info('Failed to update tech notes', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", { position: "top-center" });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };
  // ************** adding tech notes above *****************

  //**************** the code below is add part ****************
  const handlePartSelect = newPart => {
    if (newPart && newPart.part) {
      setSelectedParts(newPart);
      setParts(prev => ({
        ...prev,
        part: newPart.part,
        id: newPart.id
      }));
    } else {
      //console.error('Invalid company selected:', newCompany);
    }
  };

  const handleAddParts = async() => {
    setLoading(true);
    const isAlreadyAdded = partsList.some((part) => part.part === selectParts.part); // Convert both to numbers
    
    if (isAlreadyAdded) {
      toast.info(selectParts.part+' has already been added.', { position: 'top-center' });
      setSelectedParts('');
      setLoading(false);
      return; // Prevent adding duplicate tech
    }
    
    const bodyData = {
      status: "addParts",
      id: id,
      parts_quantity: parseInt(servicePart.quantity),
      part: selectParts.part
    };
    try {
      const response = await axios.post(`${apiUrl}/service_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success') {
        const newPartAdded = Array.isArray(response.data[1]) ? response.data[1] : [response.data[1]];
        setPartsList((prevPart) => [...prevPart, ...newPartAdded]);
        toast.success('Part added!', {
          position: 'top-center',
        });
        setSelectedParts('');
        toggleAccordion(2); // Close the accordion or perform other UI actions
      } else {
        toast.info("Failed to add part", { position: "top-center" });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", {
          position: "top-center"
        });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! You don't have access to this area. Speak to the CFO.", {
          position: "top-center"
        });
      }
    } finally {
      setLoading(false);
    }    
  }

  const handlePartsChange = (e) => {
     setServicePart({ ...servicePart, quantity: e.target.value });
  };

  const handleRemovePart = async(partId) => {
        setLoading(true);
    const bodyAddData = {
      status: "removePart",
      removePartId: partId,
    };

    try {
      const response = await axios.post(`${apiUrl}/service_crud.php`, bodyAddData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success') {
          setPartsList(partsList.filter((part) => part.id !== response.data[1]));
          toast.success('Part removed!', { position: 'top-center' });
          // toggleAccordion();
      } else {
        toast.info("Failed to remove part", { position: "top-center" });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", { position: "top-center" });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  }
//**************** the code below is add part ****************

//**************** the code below is additional tech ****************
  const handleTechChange = async (employeeId, employeeName, serviceCallId) => { 
    setLoading(true);
    const isAlreadyAdded = additionTech.some((tech) => parseInt(tech.tech_id) === parseInt(employeeId)); // Convert both to numbers
    
    if (isAlreadyAdded) {
      toast.info('This technician has already been added.', { position: 'top-center' });
      setLoading(false);
      return; // Prevent adding duplicate tech
    }
    
    if ( employeeName === serviceCalls.TECH) {
      toast.info('You cant add yourself.', { position: 'top-center' });
      setLoading(false);
      return; // Prevent adding duplicate tech
    }

    const bodyData = {
      status: "additionalTech",
      tech_id: employeeId,
      id: serviceCallId,
      tech_name: employeeName
    };

    try {
      const response = await axios.post(`${apiUrl}/service_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success') {
        const newTech = Array.isArray(response.data[1]) ? response.data[1] : [response.data[1]];
        setAdditionTech((prevTech) => [...prevTech, ...newTech]);
        toast.success('Tech added!', {
          position: 'top-center',
        });
        toggleAccordion(0); // Close the accordion or perform other UI actions
      } else {
        toast.info("Failed to add tech", { position: "top-center" });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", {
          position: "top-center"
        });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! You don't have access to this area. Speak to the CFO.", {
          position: "top-center"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTech = async (addition_tech_id) => { 
    setLoading(true);
    const bodyAddData = {
      status: "removeAdditionalTech",
      addition_tech_id: addition_tech_id,
    };
    try {
      const response = await axios.post(`${apiUrl}/service_crud.php`, bodyAddData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success') {
          setAdditionTech(additionTech.filter((tech) => tech.id !== response.data[1]));
         
          toast.success('Tech removed!', { position: 'top-center' });
          // toggleAccordion();
      } else {
        toast.info("Failed to remove tech", { position: "top-center" });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", { position: "top-center" });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };
//**************** the code above is additional tech ****************


  const handleCompleteServiceCall = async(type) => {
    if ( !hourList || (Array.isArray(hourList) && hourList.length === 0)
    ) {
      toast.info('Missing hour type.', { position: 'top-center' });
      setLoading(false);
      return;
    }
    setLoading(true);
    const bodyData = {
      status: type, 
      id: id, 
    };

    try {
      const response = await axios.post(`${apiUrl}/service_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success') {
        setServiceComplete((prevState) => ({
          ...prevState,
          [type]: !prevState[type]
        }));
        toast.success('done!', { position: 'top-center' });
        toggleAccordion(4); // Close the accordion after submission if needed
        navigate(`/t/tservlist`)
      } else {
        toast.info('Failed to update', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", { position: "top-center" });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }

  };  

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setServiceEmail((prevState) => ({
      ...prevState,
      [name]: value
    }));
    if (name === 'customerEmail') {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setIsValidEmail(emailPattern.test(value));
    }
  };

  const handleCheckboxChange = async () => {
    if (!hourList || (Array.isArray(hourList) && hourList.length === 0)) {
      toast.info('Missing hour type.', { position: 'top-center' });
      setLoading(false);
      return;
    }

    // We want to toggle *to* the next value
    const nextNoEmail = !serviceEmail.noEmail;

    setServiceEmail(prev => ({
      ...prev,
      noEmail: nextNoEmail,
      customerEmail: nextNoEmail ? 'noemail' : '',
      customerPrint: nextNoEmail ? 'noemail' : ''
    }));

    if (nextNoEmail) {
      setLoading(true);
      const bodyData = {
        status: "updateNoEmail",
        id: id,
      };
      try {
        const response = await axios.post(`${apiUrl}/service_crud.php`, bodyData, {
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.data[0] === 'success') {
          // Keep the new state already set above, or sync with backend if needed
          toast.success('No customer email set!', { position: 'top-center' });
          toggleAccordion(null);
        } else {
          toast.info("Failed to update no-email", { position: "top-center" });
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          navigate('/login');
        } else if (error.response && error.response.status === 403) {
          toast.error("Oops!! You don't have access to this area. Speak to the CFO.", { position: "top-center" });
        }
      } finally {
        setLoading(false);
      }
    } else {
      // Optionally, handle unchecking (if you want to remove noemail from DB)
      // Add your API call for unchecking here if needed
    }
  };

  const handleSubmitCustomerEmail = async() => {
    if (serviceEmail.customerEmail && serviceEmail.customerPrint) {
    setLoading(true);
    const bodyData = {
      status: "addServiceEmail", // Assuming this is the correct status for your backend
      customer_email: serviceEmail.customerEmail,
      customer_print: serviceEmail.customerPrint,
      id: id,  // Assuming id is the service call ID
    };
    try {
      const response = await axios.post(`${apiUrl}/service_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data[0] === 'success') {
        setServiceCalls((prevState) => ({
          ...prevState,
          CUSTOMEREMAIL: serviceEmail.customerEmail, CUSTOMER_PRINT: serviceEmail.customerPrint
        }));
        toast.success('Email updated!', { position: 'top-center' });
         navigate(`/t/tservsignature/${id}`)
        toggleAccordion(4); // Close the accordion after submission if needed
      } else {
        toast.info('Failed to update email', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", { position: "top-center" });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops! You don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
      // Send to backend if needed
    } else {
        toast.success('We need both email and customer\'s printed name!', { position: 'top-center' });
    }
  };

  const handleActivateSignature = () => {
    setActivateSignature(true);
    // setCustomerEmailMessage('Signature activated.');
    navigate(`/t/tservsignature/${id}`)
  };

  // Multi-file upload
  const handleMultiFileUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!folderId) {
      toast.error("No folder ID. Please create folder first.", { position: 'top-center' });
      return;
    }
    if (!selectedFiles.length) {
      toast.error("No files selected.", { position: 'top-center' });
      return;
    }

    try {
      for (const file of selectedFiles) {
        // 1) Build a FormData for each file
        const formData = new FormData();
        formData.append('status', 'uploadFile');  // or your backend route
        formData.append('folderId', folderId);
        formData.append('file', file);

        // 2) Post to your backend
        const uploadRes = await axios.post(`${apiUrl}/drive_folder.php`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (!uploadRes.data?.success) {
          toast.error(`Error uploading ${file.name}: ${uploadRes.data?.error || 'Unknown error'}`, {
            position: 'top-center'
          });
        } else {
          // success => maybe you do something
        }
      }
      toast.success("All photos uploaded!", { position: 'top-center' });
      // Clear the files & close modal
      setFilesToUpload([]);
      setIsModalFileGenOpen(false);

    } catch (err) {
      console.error('Error uploading files', err);
      toast.error('Error uploading files', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };  

  // Add photos
  const handleAddPhotosClick = async () => {
    if (!serviceCalls?.ADDRESS) {
      toast.error("No job site found!", { position: 'top-center' });
      return;
    }

    try {
      // 1) Create folder
      const folderName = serviceCalls.ADDRESS;
      const response = await axios.post(`${apiUrl}/drive_folder.php`, {
        status: 'createFolderServiceCall',
        inspection_id: serviceCalls.id,
        data: { folderName }
      });
      if (response.data?.success) {
        const newFolderId = response.data.folderId;
        setFolderId(newFolderId);       // store the folder ID
        setIsModalFileGenOpen(true);    // open the modal to pick images
      } else {
        toast.error('Error creating folder: ' + response.data.error, { position: 'top-center' });
      }

    } catch (error) {
      console.error('Error creating folder', error);
      toast.error('Failed to create folder', { position: 'top-center' });
    }
  };

  const handleFilesChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    // do something with selectedFiles...
    setSelectedFiles(selectedFiles);
  };

  const documentLink = () => {
    const expFileUrl = `${appRoot}/service_file/${serviceCalls?.file_name}`;
    window.open(expFileUrl, '_blank');  // opens in a new tab
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Header */}
      <div className="px-4 pt-4">
        <h1 className="text-xl font-semibold text-gray-900">Service Call</h1>
        <p className="text-xs text-gray-500">Tech view</p>
      </div>

      {/* Top action */}
      <div className="px-4 mt-3">
        <button
          type="button"
          onClick={handleAddPhotosClick}
          className="w-full rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          Add Photos
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="px-4 py-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 w-24 rounded bg-gray-200" />
            <div className="h-20 w-full rounded-2xl bg-gray-200" />
            <div className="h-28 w-full rounded-2xl bg-gray-200" />
          </div>
        </div>
      )}

      {/* Summary */}
      {!loading && serviceCalls && (
        <div className="px-4 py-4 space-y-4">
          <div className="rounded-2xl bg-white/95 backdrop-blur shadow ring-1 ring-gray-200 p-4">

            {/* Top row: badges */}
            <div className="flex items-center gap-2">
              {/* Type */}
              {serviceCalls.TYPE && (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
                    serviceCalls.TYPE === 'NO CHARGE'
                      ? 'bg-amber-50 text-amber-700 ring-amber-600/20'
                      : 'bg-indigo-50 text-indigo-700 ring-indigo-600/20'
                  }`}
                >
                  {serviceCalls.TYPE}
                </span>
              )}

              {/* Status (if you have one) */}
              {serviceCalls.STATUS && (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${
                    String(serviceCalls.STATUS).toLowerCase().includes('complete')
                      ? 'bg-green-50 text-green-700 ring-green-600/20'
                      : 'bg-gray-50 text-gray-700 ring-gray-300/50'
                  }`}
                >
                  {serviceCalls.STATUS}
                </span>
              )}

              <span className="ml-auto text-xs font-semibold text-gray-500">
                #{serviceCalls.id}
              </span>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              {Array.isArray(serviceCalls.additional_tech) && serviceCalls.additional_tech.length > 0 && (
              <div className="mt-1 text-xs text-gray-700">
                <span className="font-medium">Additonal Tech:</span>
                {serviceCalls.additional_tech.map((add_tech, index) => (
                  <p key={index} className="whitespace-pre-wrap">
                    <span className="font-medium text-blue-700">{add_tech.tech_name}</span>
                  </p>
                ))}
              </div>
              )}          
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              {serviceCalls.USER && (
              <div className="mt-1 text-xs text-gray-700">
                <span className="font-medium">Logged by:</span>
                <span className="whitespace-pre-wrap">{serviceCalls.USER}</span>
              </div>
              )}          
            </div>

            <div className="mt-3 grid gap-2 text-sm bg-slate-100 p-2 rounded-lg">
              {serviceCalls.TROUBLE && (
              <div className="mt-1 text-sm text-red-700">
                <span className="font-medium">PO# / Service Ticket Notes: </span>
                <span className="whitespace-pre-wrap">{serviceCalls.TROUBLE}</span>
              </div>
              )}          
            </div>

            {/* Customer / site */}
            <div className="mt-3">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {serviceCalls.CUSTOMER || '—'}
              </div>
              <div className="text-xs text-gray-600 truncate">
                {serviceCalls.ADDRESS || '—'}
              </div>
              <div className="mt-1 text-xs text-gray-600">
                {/* Called by & phone */}
                {serviceCalls.CALLED_BY && (
                  <span className="mr-2">
                    <span className="font-medium text-gray-700">Called by:</span> {serviceCalls.CALLED_BY}
                  </span>
                )}
                {serviceCalls.PHONE && (
                  <span className="text-gray-600">
                    <span className="font-medium text-gray-700 ml-2">Tel:</span>{' '}
                    <PhoneNumber number={serviceCalls.PHONE} />
                  </span>
                )}
              </div>
            </div>

            {/* Chips row: date / time (regular size) */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-gray-50 px-3 py-1 text-sm font-medium text-gray-700 ring-1 ring-gray-200">
                {serviceCalls.SCHEDULE_DATE
                  ? serviceCalls.SCHEDULE_DATE
                  : (serviceCalls.DATE ? <ShortDateFormat date={serviceCalls.DATE} /> : '—')}
              </span>

              {(serviceCalls.TIME || serviceCalls.end_time) && (
                <span className="inline-flex items-center rounded-full bg-gray-50 px-3 py-1 text-sm font-medium text-gray-700 ring-1 ring-gray-200">
                  {serviceCalls.TIME || '—'}
                  {serviceCalls.end_time ? ` – ${serviceCalls.end_time}` : ''}
                </span>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={() => setIsInternalOpen(true)}
              className="flex-1 rounded-xl border border-gray-300 bg-white px-4 py-2.5 
                        text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-100 
                        focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Internal Notes
            </button>
            {/* 
                      <span className='text-gray-700'>
                        <ServiceGenFileLocation expFile={serviceCalls?.file_name} where={'service_file'} />
                      </span> */}
                        
            {/* 
              if(expFile){
                var expFileUrl = `${appRoot}service_file/${serviceCalls?.file_name}`;
                return <a href={`${expFileUrl}`} target='_blank'><PaperClipIcon className='h-5'/></a>
              }        */}
          {serviceCalls?.file_name && (
            <button
              type="button"
              onClick={documentLink}
              className="flex-1 rounded-xl border border-orange-300 bg-orange-600 px-4 py-2.5 
                        text-sm font-semibold text-white shadow-sm hover:bg-orange-700 
                        focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              Document
            </button>
          )}

            <button
              type="button"
              onClick={openInMaps}
              className="flex-1 rounded-xl border border-gray-300 bg-gray-600 px-4 py-2.5 
                        text-sm font-semibold text-white shadow-sm hover:bg-gray-700 
                        focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Open in Maps
            </button>
          </div>



          {/* Additional Tech */}
          <Accordion
            title="Additional Tech"
            content={
              <>
                <ul className="mb-3 space-y-2">
                  {additionTech.map((tech) => (
                    <li key={tech.id} className="flex items-center gap-2 rounded-lg bg-gray-50 p-2">
                      <button onClick={() => handleRemoveTech(tech.id)} className="text-red-600">
                        <TrashIcon className="h-5" />
                      </button>
                      <span className="text-sm text-gray-900">{tech.tech_name}</span>
                    </li>
                  ))}
                </ul>

                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={addTech.additional_tech || ''}
                  onChange={(e) => {
                    const selectedOption = e.target.options[e.target.selectedIndex];
                    const employeeId = e.target.value;
                    const employeeName = selectedOption.textContent;
                    handleTechChange(employeeId, employeeName, serviceCalls.id);
                  }}
                >
                  <option value="">Select Employee</option>
                  {Array.isArray(employees) && employees.length > 0 ? (
                    employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No employees found</option>
                  )}
                </select>
              </>
            }
            isOpen={openIndex === 0}
            toggle={() => setOpenIndex(openIndex === 0 ? null : 0)}
          />

          {/* Hours */}
          <Accordion
            title="Add Hours (required)"
            content={
              <>
                <ul className="mb-3 space-y-2">
                  {hourList.map((hrs, i) => (
                    <li key={hrs.id ?? i} className="flex items-center gap-2 rounded-lg bg-gray-50 p-2">
                      <button onClick={() => handleDeleteHours(hrs.id)} className="text-red-600">
                        <TrashIcon className="h-5" />
                      </button>
                      <span className="text-sm text-gray-900">{hrs.total_hours} hrs — {hrs.hour_type}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="Hrs"
                    value={service.total_hours || ''}
                    onChange={handleHoursChange}
                    className="w-1/3 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                  <select
                    value={service.hour_type || ''}
                    onChange={handleHourTypeChange}
                    className="w-1/3 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="Regular">Regular</option>
                    <option value="Overtime">Overtime</option>
                    <option value="Holiday">Holiday/Sunday</option>
                  </select>
                  <button
                    onClick={handleSubmitHours}
                    className="w-1/3 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Add Hours
                  </button>
                </div>
              </>
            }
            isOpen={openIndex === 1}
            toggle={() => setOpenIndex(openIndex === 1 ? null : 1)}
          />

          {/* Parts */}
          <Accordion
            title="Add Parts (required)"
            content={
              <>
                <ul className="mb-3 space-y-2">
                  {partsList.map((x) => (
                    <li key={x.id} className="flex items-center gap-2 rounded-lg bg-gray-50 p-2">
                      <button onClick={() => handleRemovePart(x.id)} className="text-red-600">
                        <TrashIcon className="h-5" />
                      </button>
                      <span className="text-sm text-gray-900">{x.part} × {x.quantity}</span>
                    </li>
                  ))}
                </ul>

                <div className="mb-3 flex items-start gap-2">
                  <div className="flex-1">
                    <SelectParts
                      label="Find part"
                      items={parts}
                      selectedItem={selectParts}
                      setSelectedItem={handlePartSelect}
                      apiUrl={`${apiUrl}/service_crud.php`}
                    />
                  </div>
                  <input
                    type="number"
                    name="quantity"
                    placeholder="Qty"
                    value={servicePart.quantity}
                    onChange={handlePartsChange}
                    required
                    className="w-20 rounded-lg border border-gray-300 px-2 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={handleAddParts}
                  >
                    Add Parts
                  </button>
                  <button
                    className="rounded-lg bg-rose-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-500"
                    onClick={() => { setSkipPart(true); setOpenIndex(null); }}
                  >
                    Skip Parts
                  </button>
                </div>
              </>
            }
            isOpen={openIndex === 2}
            toggle={() => setOpenIndex(openIndex === 2 ? null : 2)}
          />

          {/* Tech Notes */}
          <Accordion
            title="Tech’s Notes (required)"
            content={
              <>
                <textarea
                  value={techNotes || ''}
                  onChange={handleTechNotesChange}
                  rows={5}
                  className="mt-1 w-full rounded-xl border border-gray-300 bg-white p-3 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Add your notes…"
                />
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={handleSubmitTechNotes}
                    className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Submit Notes
                  </button>
                </div>
              </>
            }
            isOpen={openIndex === 3}
            toggle={() => setOpenIndex(openIndex === 3 ? null : 3)}
          />

          {/* Customer Email / Signature */}
          <Accordion
            title="Customer’s Email / Signature"
            content={
              <>
                {!serviceEmail.noEmail && (
                  <>
                    {!isValidEmail && <p className="text-red-500 text-sm">Valid Email Format</p>}

                    <input
                      type="email"
                      name="customerEmail"
                      value={serviceEmail.customerEmail || ''}
                      onChange={handleEmailChange}
                      placeholder="email@email.com"
                      className={`w-full rounded-lg border px-4 py-2 text-sm mb-3 ${
                        !isValidEmail ? 'border-red-500' : 'border-gray-300'
                      } focus:border-indigo-500 focus:ring-indigo-500`}
                    />
                    <input
                      type="text"
                      name="customerPrint"
                      value={serviceEmail.customerPrint || ''}
                      onChange={handleEmailChange}
                      placeholder="Print Name"
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm mb-3 focus:border-indigo-500 focus:ring-indigo-500"
                    />

                    <div className="flex items-center gap-2">
                      <button
                        className={`rounded-lg px-4 py-2 text-sm text-white ${
                          serviceEmail.customerEmail && serviceEmail.customerPrint && activateSignature
                            ? 'bg-indigo-600 hover:bg-indigo-700'
                            : 'bg-indigo-300 cursor-not-allowed'
                        }`}
                        onClick={handleSubmitCustomerEmail}
                        disabled={!serviceEmail.customerEmail || !serviceEmail.customerPrint || !activateSignature}
                      >
                        Submit Email
                      </button>

                      {submitEmailButton && (
                        <button
                          className={`rounded-lg px-4 py-2 text-sm text-white ${
                            activateSignature ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-300 cursor-not-allowed'
                          }`}
                          onClick={handleActivateSignature}
                          disabled={!activateSignature}
                        >
                          View Signature
                        </button>
                      )}
                    </div>

                    {!activateSignature && (
                      <div className="mt-2 text-xs text-rose-600">Hours missing!!</div>
                    )}
                  </>
                )}

                <div className="mt-4 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={serviceEmail.noEmail}
                    onChange={handleCheckboxChange}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label className="text-sm text-gray-700">No Customer Available for Signature</label>
                </div>

                {(serviceEmail.noEmail || serviceCalls.CUSTOMEREMAIL === 'noemail') && (
                  <div className="mt-5 space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="completeTicket"
                        checked={serviceComplete.completeTicket}
                        onChange={() => handleCompleteServiceCall('completeTicket')}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        disabled={!keyController}
                      />
                      <span className="text-sm">Fully completed the service call</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="completeTicket"
                        checked={serviceComplete.notCompletedTicket}
                        onChange={() => handleCompleteServiceCall('notCompletedTicket')}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        disabled={!keyController}
                      />
                      <span className="text-sm text-rose-600">(Not fully completed the service call)</span>
                    </label>

                    {customerEmailMessage && (
                      <div className="text-emerald-600 text-sm">{customerEmailMessage}</div>
                    )}
                  </div>
                )}
              </>
            }
            isOpen={openIndex === 4}
            toggle={() => setOpenIndex(openIndex === 4 ? null : 4)}
          />
        </div>
      )}
      
      {/* Sticky footer actions */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur">
        <div className="mx-auto w-full max-w-md px-4 py-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={()=> navigate('/t/tservlist')}
              className="w-1/2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Service Queue
            </button>

            <button
              type="button"
              onClick={()=> navigate('/t/tech_schedule')}
              className="w-1/2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Tech Schedules
            </button>
          </div>
        </div>
      </div>

      {isInternalOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsInternalOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-md rounded-t-2xl bg-white/95 backdrop-blur-md shadow-2xl ring-1 ring-gray-200">
            <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-gray-300" />
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Internal Notes</h3>
              <button
                type="button"
                onClick={() => setIsInternalOpen(false)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Close
              </button>
            </div>
            <div className="px-4 py-4 max-h-[65vh] overflow-y-auto">
              <div className="whitespace-pre-wrap text-sm text-gray-900">
                <LinkText text={serviceCalls?.NOTES?.trim() ? serviceCalls.NOTES : '— No internal notes —'} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo Upload Bottom Sheet */}
      {isModalFileGenOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsModalFileGenOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-md rounded-t-2xl bg-white/95 backdrop-blur-md shadow-2xl ring-1 ring-gray-200">
            <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-gray-300" />
            <div className="px-4 py-4">
              <h2 className="text-base font-semibold text-gray-900">
                Upload Photos for {serviceCalls?.ADDRESS || 'Job Site'}
              </h2>
              <form onSubmit={handleMultiFileUpload} className="mt-3 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Select images (Max 2MB each)
                  </label>
                  <input
                    type="file"
                    accept="image/png, image/jpeg"
                    multiple
                    onChange={handleFilesChange}
                    className="mt-2 block w-full text-sm"
                  />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => { setIsModalFileGenOpen(false); setFilesToUpload([]); }}
                    className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {loading ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div> ///ending div bracket for below return
  );
};

export default TechServiceDetail;
